import { Icon } from "@vds/icons";
import {
  getEventsIconOnEventsTile,
  getBannerEventsIconOnEventsTile,
  getNotificationIconImage,
} from "../model/DashboardFilterService";
import { Button } from "@vds/buttons";
import { useNavigate, useLocation } from "react-router-dom";
import { getEventLocationDescription } from "../global/utility/incidentsUtils";
import {
  BannerCommonNotify,
  BannerNotificationIcon,
  BannerNotifiCommonSegment,
  CloseNotifiBanner,
  NotifiBannerWrapper,
  TitleNSubTitleWrapper,
  TitleSubTitleSegment,
  BannerNotifiSubCommonSegment,
  GotoEventNotifiBanner
} from "./NotificationStyles";
import {
  bannerTypes,
  eventAlerts,
  getNotificationsList,
  notificationTypes,
} from "./NotificationConfig";
import { Notification } from "@vds/notifications";
import { useSelector, useDispatch } from "react-redux";
import { useWindowSize } from "../global/hooks/viewportHooks";
import { breakpoints } from "../styles/breakpoints";
import { convertDateFormat } from "../global/utility/Utils";

/*
--------------------------------------------------------------------------
Usage Guidelines :: 
  <NotificationBanner notificationInfo={{priority: 'CRITICAL'}} setShowNotification={setShowNotification}/>
Properties ::
setShowNotification - callBack to set notification state true / false
notificationInfo - must have properties as below :
  {type, camera, site, state: {}} // Camera Offline
  {type, companion, site, state: {}} // Companion Offline
------------------------------------------------------------------------------
*/

const NotifyBannerClose = ({ updateShowNotify, children }) => {
  return (
    <CloseNotifiBanner onClick={updateShowNotify}>{children}</CloseNotifiBanner>
  );
};

const NotificationBanner = ({ notificationInfo, setShowNotification }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { width: windowWidth } = useWindowSize();
  const buttonSize = windowWidth < parseInt(breakpoints.tablet) ? "small" : "large";

  const notifications = getNotificationsList(notificationInfo);
  const notificationType = Object.values(notificationTypes).includes(
    notificationInfo?.type
  )
    ? notificationInfo?.type
    : notificationTypes[16];
  const selNotificationObj = notifications.find(
    (notifObj) => notifObj.type === notificationType
  );
  const tempBannerType =
    (notificationInfo?.priority && notificationInfo?.priority.toLowerCase()) || 
    (notificationInfo?.severity && notificationInfo?.severity.toLowerCase());
  const bannerType = bannerTypes.includes(tempBannerType)
    ? tempBannerType
    : bannerTypes[1];
  const isEventAlert = eventAlerts.includes(notificationType);
  const userGroupList = JSON.parse(
    sessionStorage.getItem("userGroupsResponse")
  );
  const isDismissible = notificationInfo?.isDismissible !== false;
  const shouldHideCloseButton = notificationInfo?.hideCloseButton === true;

  const getTitle = () => {
    let dataObj = {
      camera: notificationInfo?.camera,
      site: notificationInfo?.site,
      companion: notificationInfo?.companion,
    };
  
    switch (notificationType) {
      case notificationTypes[16]: // 'Event Alert':
        return (
          // (bannerType == bannerTypes[0] ? bannerTypes[0] + " " : "") +
          selNotificationObj?.title 
        );
      case notificationTypes[17]: // 'Event Alerts':
        return (
          (bannerType == bannerTypes[0] ? bannerTypes[0] + " " : "") +
          selNotificationObj?.title
        );
      default:
        return replacePlaceholders(selNotificationObj?.title, dataObj);
    }
  };
  const getSecondTitle = () => {
    switch (notificationType) {
      case notificationTypes[16]: // 'Event Alert':
        const formatted = convertDateFormat(notificationInfo?.createdAt);
        return formatted?.formatWithTime || "";
      
      default:
        return "";
    }
  };

  const getSubTitle = () => {
    let dataObj = {
      application: notificationInfo?.application,
      count: notificationInfo?.count,
      companion: notificationInfo?.companion,
      event: notificationInfo?.event,
      x_date: notificationInfo?.x_date,
      y_stater: notificationInfo?.y_stater,
      z_intelligence: notificationInfo?.z_intelligence,
      license: notificationInfo?.license,
    };

    switch (notificationType) {
      case notificationTypes[16]: // 'Event Alert':
        return getEventLocationDescription(
          notificationInfo?.applicationId,
          notificationInfo?.deviceLoc,
          notificationInfo?.description,
          notificationInfo?.title
        );
         case notificationTypes[95]: // 'CCD Offline':
        return notificationInfo?.subtitle;        
      default:
        return notificationInfo?.subtitle || replacePlaceholders(selNotificationObj?.subtitle, dataObj);
    }
  };

  const getNotificationIcon = () => {
    const isInfo = bannerType === bannerTypes[2];
    if (notificationType == notificationTypes[16]) {
      // 'Event Alert':
      // return getEventsIconOnEventsTile(notificationInfo?.title, isInfo);
       return getBannerEventsIconOnEventsTile(notificationInfo);
    } else if (notificationType == notificationTypes[17]) {
      // 'Event Alerts':
      return;
    } else {
      return getNotificationIconImage(bannerType); // error, info, warning
    }
  };

  const doButtonAction = (url = null, stateObj = {}) => {
    if (!notificationInfo?.dispatchAction && (isDismissible || (!url && !shouldHideCloseButton))) {
        setShowNotification(false);
    }
    if (url) {
      navigate(url, { state: stateObj });
    } else if (notificationInfo?.dispatchAction) {
      const isCameraInfoPage = location.pathname === "/cameraInfoPage";
      const action =
        isCameraInfoPage && notificationInfo.cameraInfoDispatchAction
          ? notificationInfo.cameraInfoDispatchAction
          : notificationInfo.dispatchAction;
      dispatch(action);
    }
    // const usernameExists = checkUsernameExists(username);
    // if (selNotificationObj.type === notificationTypes[7]) {
    //   if (usernameExists) {
    //     navigate(url);
    //   } else {
    //     alert('User not found!');
    //   }
    // } else {
    // navigate(url);
    // }
    return;
  };

  const replacePlaceholders = (templateString, data) => {
    return (
      templateString &&
      templateString.replace(/\[([^\]]+)\]/g, (match, key) => {
        return data.hasOwnProperty(key) && data[key] !== undefined
          ? data[key]
          : `[${key}]`;
      })
    );
  };

  const buttonDataObj = [];
  if (selNotificationObj?.buttonLabel) {
    buttonDataObj.push({
      children: selNotificationObj.buttonLabel,
      onClick: () =>
        doButtonAction(
          selNotificationObj?.redirectUrl,
          selNotificationObj?.state
        ),
      use: isEventAlert ? "primary" : "secondary",
    });
  }
  if (selNotificationObj?.extraButtonLabel) {
    buttonDataObj.push({
      children: selNotificationObj.extraButtonLabel,
      onClick: () => doButtonAction(selNotificationObj?.extraRedirectUrl),
    });
  }

  // const checkUsernameExists = (username) => {
  //   return userGroupList?.some(usersGroup =>
  //     usersGroup?.users?.some(user => `${user.firstname} ${user.lastname}` === username)
  //   );
  // };

  const formattedBannerType = (bannerType) =>
    bannerType == bannerTypes[0] ? "error" : bannerType;

  return !isEventAlert ? (
    // ------------------- Common Notification Section ----------------------

    <BannerCommonNotify
      bannerType={bannerType}
      isEventAlert={isEventAlert}
      svgPath={getNotificationIcon()}
    >
      <Notification
        className="main-notification-banner"
        type={formattedBannerType(bannerType)}
        title={getTitle()}
        subtitle={getSubTitle()}
        surface="light"
        disableAnimation={false}
        fullBleed={true}
        hideCloseButton={notificationInfo?.hideCloseButton != null ? shouldHideCloseButton : !isDismissible}
        disableFocus={true}
        inline={false}
        layout={null}
        buttonData={buttonDataObj}
        onCloseButtonClick={() => {
          setShowNotification(false);
        }}
      />
    </BannerCommonNotify>
  ) : (
    // -------------------------- Event Notification Section ----------------------
    <NotifiBannerWrapper bannerType={bannerType} isEventAlert={isEventAlert}>
      <TitleNSubTitleWrapper>
        {/* notification icon */}
        <BannerNotificationIcon src={getNotificationIcon()} />

        <TitleSubTitleSegment>
          {/* show title */}
          <BannerNotifiCommonSegment segment="title">
            {getTitle()}
          </BannerNotifiCommonSegment>
          {/* show sub-title */}
          {getSecondTitle() !== "" && <BannerNotifiSubCommonSegment segment="subtitle">
            {getSecondTitle()}
          </BannerNotifiSubCommonSegment>}
          <BannerNotifiCommonSegment className="subtitle-desc" segment="subtitle">
            {getSubTitle()}
          </BannerNotifiCommonSegment>
        </TitleSubTitleSegment>
      </TitleNSubTitleWrapper>

      {/* show action button */}
      {selNotificationObj?.buttonLabel && (
        <GotoEventNotifiBanner>
        <Button
          onClick={() =>
            doButtonAction(
              selNotificationObj?.redirectUrl,
              selNotificationObj?.state
            )
          }
          use={isEventAlert ? "primary" : "secondary"}
          size={buttonSize}
        >
          {selNotificationObj?.buttonLabel}
        </Button>
        
        </GotoEventNotifiBanner>
      )}

      {/* show extra button based on condition */}
      {bannerType === bannerTypes[2] &&
        notificationType === notificationTypes[16] && (
          <Button use="secondary" size={buttonSize} onClick={() => doButtonAction()}>
            {selNotificationObj?.extraButtonLabel}
          </Button>
        )}

      {/* close icon button */}
      {isDismissible && (
        <NotifyBannerClose updateShowNotify={() => doButtonAction()}>
          <Button
          use={"secondary"}
          size={buttonSize}
        >
          {"Dismiss"}
        </Button>
        </NotifyBannerClose>
      )}
    </NotifiBannerWrapper>
  );
};

export default NotificationBanner;
